import { motion, useMotionValue, useSpring, useTransform } from 'framer-motion'
import { useEffect } from 'react'

interface Props {
  cx: number
  cy: number
  radius: number
  startAngle: number
  endAngle: number
  fill: string
  className?: string
  onMouseEnter: () => void
  onClick: () => void
  onMouseLeave: () => void
}

export const AnimatedPieSlice: React.FC<Props> = ({
  cx,
  cy,
  radius,
  startAngle,
  endAngle,
  fill,
  className,
  onMouseEnter,
  onClick,
  onMouseLeave,
}) => {
  const startMv = useMotionValue(startAngle)
  const endMv = useMotionValue(endAngle)
  const animStart = useSpring(startMv, { stiffness: 500, damping: 40, mass: 0.8, restDelta: 0.001 })
  const animEnd = useSpring(endMv, { stiffness: 500, damping: 40, mass: 0.8, restDelta: 0.001 })
  /**
   * Motion value of the d-path.
   */
  const d = useTransform<number, string>([animStart, animEnd], ([s, e]) => makeSlicePath(cx, cy, radius, s, e))

  // Whenever props change, animate to the new angles
  useEffect(() => {
    startMv.set(startAngle)
    endMv.set(endAngle)
  }, [startAngle, endAngle, startMv, endMv])

  return (
    <motion.path
      className={className}
      d={d}
      fill={fill}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onClick={onClick}
    />
  )
}

/**
 * Given center coordinates, radius, and start and end angles, produces a path for a pie slice.
 */
export const makeSlicePath = (cx: number, cy: number, radius: number, start: number, end: number) => {
  const x1 = cx + Math.cos(start) * radius
  const y1 = cy + Math.sin(start) * radius
  const x2 = cx + Math.cos(end) * radius
  const y2 = cy + Math.sin(end) * radius

  // Check if this is a full circle (within a small epsilon to handle floating point imprecision).
  const isFullCircle = Math.abs(end - start - 2 * Math.PI) < 0.0001

  if (isFullCircle) {
    // For a full circle, we need to use two arcs.
    const midX = cx + Math.cos(start + Math.PI) * radius
    const midY = cy + Math.sin(start + Math.PI) * radius
    return [
      `M ${cx} ${cy}`,
      `L ${x1} ${y1}`,
      `A ${radius} ${radius} 0 1 1 ${midX} ${midY}`,
      `A ${radius} ${radius} 0 1 1 ${x1} ${y1}`,
      'Z',
    ].join(' ')
  }

  const largeArc = end - start > Math.PI ? 1 : 0
  return [`M ${cx} ${cy}`, `L ${x1} ${y1}`, `A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2}`, 'Z'].join(' ')
}

/**
 * Given center coordinates, radius, and start and end angles, produces a path for a pie slice halo.
 */
export const makeHaloPath = (cx: number, cy: number, radius: number, start: number, end: number) => {
  const x1 = cx + Math.cos(start) * radius
  const y1 = cy + Math.sin(start) * radius
  const x2 = cx + Math.cos(end) * radius
  const y2 = cy + Math.sin(end) * radius

  // Check if this is a full circle (within a small epsilon to handle floating point imprecision).
  const isFullCircle = Math.abs(end - start - 2 * Math.PI) < 0.0001

  if (isFullCircle) {
    // For a full circle, we need to use two arcs.
    const midX = cx + Math.cos(start + Math.PI) * radius
    const midY = cy + Math.sin(start + Math.PI) * radius
    return [
      `M ${cx} ${cy}`,
      `L ${x1} ${y1}`,
      `A ${radius} ${radius} 0 1 1 ${midX} ${midY}`,
      `A ${radius} ${radius} 0 1 1 ${x1} ${y1}`,
      'Z',
    ].join(' ')
  }

  const largeArc = end - start > Math.PI ? 1 : 0
  return [`M ${cx} ${cy}`, `L ${x1} ${y1}`, `A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2}`, 'Z'].join(' ')
}
